import React, { useRef, useState } from "react";
import { Upload, FileText, Trash2, Loader2, ExternalLink } from "lucide-react";
import { api, resolveImage } from "../../lib/api";

// Stores list of { name, url } objects.
const PdfUploader = ({ items = [], onChange, label = "Project PDFs" }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const onFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const next = [...items];
      for (const file of files) {
        const { url } = await api.upload(file);
        next.push({ name: file.name, url });
      }
      onChange(next);
    } catch (e) {
      alert(e.message || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const rename = (idx, name) => {
    const next = [...items];
    next[idx] = { ...next[idx], name };
    onChange(next);
  };

  return (
    <div>
      {label && (
        <div className="text-xs font-medium uppercase tracking-wider text-[color:var(--muted)] mb-2">
          {label}
        </div>
      )}

      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-black/25 px-5 py-8 text-center text-sm text-[color:var(--muted)]">
          No PDFs yet. Upload case-study decks, brand books, or anything else.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div
              key={i}
              className="flex items-center gap-3 p-3 rounded-xl bg-white border border-black/10"
            >
              <span className="w-10 h-10 rounded-lg bg-[color:var(--coral-soft)] grid place-items-center shrink-0">
                <FileText className="w-5 h-5 text-[color:var(--ink)]" />
              </span>
              <div className="flex-1 min-w-0">
                <input
                  value={it.name || ""}
                  onChange={(e) => rename(i, e.target.value)}
                  className="w-full bg-transparent border-b border-transparent hover:border-black/15 focus:border-[color:var(--coral)] outline-none text-sm font-medium py-1 transition-colors"
                  placeholder="Untitled.pdf"
                />
                <a
                  href={resolveImage(it.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-[color:var(--muted)] hover:text-[color:var(--coral)] inline-flex items-center gap-1"
                >
                  Open <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <button
                type="button"
                onClick={() => remove(i)}
                className="w-9 h-9 rounded-full border border-black/15 hover:bg-black/5 grid place-items-center"
                aria-label="Remove"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3">
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          multiple
          className="hidden"
          onChange={(e) => onFiles(Array.from(e.target.files || []))}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-black/20 text-sm hover:border-black/50 disabled:opacity-60"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" /> Upload PDF{items.length > 0 ? "s" : ""}
            </>
          )}
        </button>
        <span className="ml-3 text-xs text-[color:var(--muted)]">Max 25 MB per file</span>
      </div>
    </div>
  );
};

export default PdfUploader;
